import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Loader2, Wrench, AlertTriangle, RefreshCw, Droplet, CircleDot, CalendarCheck, Disc3, Settings2 } from "lucide-react";
import { formatBRL, formatKM } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hoje = new Date().toISOString().slice(0, 10);

const TIPOS = {
  oleo: { label: "Troca de óleo", icon: Droplet },
  pneus: { label: "Pneus", icon: CircleDot },
  revisao: { label: "Revisão", icon: CalendarCheck },
  freios: { label: "Freios", icon: Disc3 },
  outros: { label: "Outros", icon: Settings2 },
};

const STATUS_ALERTA = {
  vencido: { label: "Vencido", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  proximo: { label: "Próximo", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  ok: { label: "Em dia", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  registrado: { label: "Registrado", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
};

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const empty = { veiculo_id: "", tipo: "revisao", descricao: "", data: hoje, km_veiculo: "", custo: "", proxima_data: "", proximo_km: "" };

export const ManutencaoTab = ({ onUnauthorized }) => {
  const [items, setItems] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const [m, v] = await Promise.all([
        axios.get(`${API}/maintenance`, { headers }),
        axios.get(`${API}/vehicles/admin`, { headers }),
      ]);
      setItems(m.data);
      setVehicles(v.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setItems([]); toast.error("Erro ao carregar manutenções."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendentes = (items || []).filter((i) => i.status_alerta === "vencido" || i.status_alerta === "proximo");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      veiculo_id: form.veiculo_id,
      tipo: form.tipo,
      descricao: form.descricao || "",
      data: form.data,
      km_veiculo: form.km_veiculo ? Number(form.km_veiculo) : null,
      custo: form.custo ? Number(form.custo) : 0,
      proxima_data: form.proxima_data || null,
      proximo_km: form.proximo_km ? Number(form.proximo_km) : null,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/maintenance/${form.id}`, payload, { headers });
        toast.success("Manutenção atualizada.");
      } else {
        await axios.post(`${API}/maintenance`, payload, { headers });
        toast.success(payload.custo > 0 ? "Manutenção registrada e custo lançado na ficha do carro." : "Manutenção registrada.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro ao salvar manutenção.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/maintenance/${id}`, { headers });
      setItems((is) => is.filter((i) => i.id !== id));
      toast.success("Manutenção removida.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao remover manutenção.");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fmtData = (d) => d?.split("-").reverse().join("/");

  return (
    <div data-testid="manutencao-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Manutenção por veículo</h1>
        <div className="flex gap-2">
          <button onClick={load} data-testid="maintenance-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
          <button
            onClick={() => setForm({ ...empty })} data-testid="maintenance-new-button"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
          >
            <Plus className="w-4 h-4" /> Nova manutenção
          </button>
        </div>
      </div>

      {pendentes.length > 0 && (
        <div className="glass border-amber-500/40 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="maintenance-alertas">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-300">Atenção: {pendentes.length} manutenção(ões) pedindo cuidado</p>
            <ul className="mt-1 space-y-0.5 text-slate-300 text-xs">
              {pendentes.map((p) => (
                <li key={p.id} data-testid={`maintenance-alerta-${p.id}`}>
                  <span className={p.status_alerta === "vencido" ? "text-red-400 font-semibold" : "text-amber-400 font-semibold"}>
                    {STATUS_ALERTA[p.status_alerta].label}
                  </span>
                  {" — "}{p.veiculo_nome}: {TIPOS[p.tipo]?.label || p.tipo}
                  {p.proximo_km ? ` (previsto p/ ${formatKM(p.proximo_km)} km)` : ""}
                  {p.proxima_data ? ` (previsto p/ ${fmtData(p.proxima_data)})` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <AnimatePresence>
        {form && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="glass rounded-3xl p-6 sm:p-8 mb-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="maintenance-form"
          >
            <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-white">{form.id ? "Editar manutenção" : "Nova manutenção"}</h2>
              <button type="button" onClick={() => setForm(null)} data-testid="maintenance-form-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <select required className={inputCls} value={form.veiculo_id} onChange={set("veiculo_id")} data-testid="maintenance-input-veiculo">
              <option value="">Veículo*</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.nome} ({formatKM(v.km)} km)</option>)}
            </select>
            <select required className={inputCls} value={form.tipo} onChange={set("tipo")} data-testid="maintenance-input-tipo">
              {Object.entries(TIPOS).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
            </select>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Data realizada*</label>
              <input className={inputCls} type="date" required value={form.data} onChange={set("data")} data-testid="maintenance-input-data" />
            </div>
            <input className={inputCls} type="number" placeholder="KM do veículo na manutenção" value={form.km_veiculo} onChange={set("km_veiculo")} data-testid="maintenance-input-km" />
            <input className={`${inputCls} sm:col-span-2`} placeholder="Descrição (ex: óleo 5W30 + filtro)" value={form.descricao} onChange={set("descricao")} data-testid="maintenance-input-descricao" />
            <input className={inputCls} type="number" placeholder="Custo (R$) — lança na ficha do carro" value={form.custo} onChange={set("custo")} data-testid="maintenance-input-custo" />
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Próxima data prevista (alerta)</label>
              <input className={inputCls} type="date" value={form.proxima_data} onChange={set("proxima_data")} data-testid="maintenance-input-proxima-data" />
            </div>
            <input className={inputCls} type="number" placeholder="Próximo KM previsto (alerta)" value={form.proximo_km} onChange={set("proximo_km")} data-testid="maintenance-input-proximo-km" />
            <div className="sm:col-span-2 lg:col-span-4 flex gap-3">
              <button type="submit" disabled={saving} data-testid="maintenance-save-button" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {form.id ? "Salvar alterações" : "Registrar manutenção"}
              </button>
              <button type="button" onClick={() => setForm(null)} className="glass text-white text-sm font-semibold px-6 py-3 rounded-full">Cancelar</button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {items === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="maintenance-empty">Nenhuma manutenção registrada. Registre trocas de óleo, pneus e revisões para receber alertas antes de cada locação.</p>
      ) : (
        <div className="space-y-3" data-testid="maintenance-list">
          {items.map((m) => {
            const T = TIPOS[m.tipo] || TIPOS.outros;
            const S = STATUS_ALERTA[m.status_alerta] || STATUS_ALERTA.registrado;
            return (
              <div key={m.id} className="glass rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`maintenance-card-${m.id}`}>
                <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 shrink-0">
                  <T.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                    {m.veiculo_nome}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${S.cls}`} data-testid={`maintenance-status-${m.id}`}>{S.label}</span>
                  </p>
                  <p className="text-sm text-slate-400 truncate">
                    {T.label}{m.descricao ? ` — ${m.descricao}` : ""} · {fmtData(m.data)}
                    {m.km_veiculo ? ` · ${formatKM(m.km_veiculo)} km` : ""}
                    {m.custo > 0 ? ` · ${formatBRL(m.custo)}` : ""}
                  </p>
                  {(m.proximo_km || m.proxima_data) && (
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Próxima: {m.proximo_km ? `${formatKM(m.proximo_km)} km` : ""}{m.proximo_km && m.proxima_data ? " ou " : ""}{m.proxima_data ? fmtData(m.proxima_data) : ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setForm({ ...m, km_veiculo: m.km_veiculo ?? "", custo: m.custo || "", proxima_data: m.proxima_data || "", proximo_km: m.proximo_km ?? "" })}
                    data-testid={`maintenance-edit-${m.id}`}
                    className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors" aria-label="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(m.id)} data-testid={`maintenance-delete-${m.id}`}
                    className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors" aria-label="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
